import {Alert, Button, Choice, DataStatus, Separator} from 'platform/components';
import {Box, HStack, Heading, Hide, Scroll, Show, Spinner, VStack} from 'platform/foundation';

import {defaultTo} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {
  Basket,
  BasketMechanic,
  BasketSummary,
  EitherQuantityOrError,
  getOptionsFromMechanics,
  HiddenMechanic,
  useBasketItems,
} from '@dms/shared';

import {Either, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CorrectionBasketItem} from '../types/CorrectionBasketItem';
import {CorrectionBasketItemEditingDetails} from '../types/CorrectionBasketItemEditingDetails';
import {AfterSalesCorrectionBasketItem} from './AfterSalesCorrectionBasketItem';

const BASKET_TITLE_HEIGHT_IN_REM = 3.5;

export interface AfterSalesCorrectionBasketProps<
  T extends CorrectionBasketItem,
> extends RequiredTestIdProps {
  basket: Basket<T>;
  mechanic: Either<BasketMechanic, HiddenMechanic>;
  onQuantityChange: (itemId: string, quantity: EitherQuantityOrError) => Promise<void>;
  onEdit: (editingDetails: CorrectionBasketItemEditingDetails) => void;
  onDelete: (itemsIds: string[]) => Promise<void>;
}

export function AfterSalesCorrectionBasket<T extends CorrectionBasketItem>(
  props: AfterSalesCorrectionBasketProps<T>
) {
  const {
    basketItems,
    basketItemsCount,
    hasSomeBasketItemsSelected,
    hasAllBasketItemsSelected,
    onSelectBasketItem,
    onSelectAllBasketItems,
    onBulkDeleteBasketItems,
  } = useBasketItems({basketItems: props.basket?.items, onDeleteBasketItems: props.onDelete});

  const basketTitle = `${i18n.t('general.labels.addedItems')} (${basketItemsCount})`;

  const basketTotalPrice = props.basket?.totalPrice;

  return (
    <Box
      borderRadius="small"
      boxShadow="elevation_1"
      backgroundColor="palettes.white.10.100"
      height="100%"
      data-testid={suffixTestId('wrapper', props)}
    >
      <VStack height="100%">
        <Box padding={4}>
          <HStack spacing={2} align="center">
            <Heading size={4} data-testid={suffixTestId('basketTitle', props)}>
              {basketTitle}
            </Heading>
            <Show when={props.basket.hasPendingRequest}>
              <Spinner size="small" data-testid={suffixTestId('pendingRequestSpinner', props)} />
            </Show>
          </HStack>
        </Box>
        <Separator spacing={0} />
        <Box padding={4} height="100%" maxHeight={`calc(100% - ${BASKET_TITLE_HEIGHT_IN_REM}rem)`}>
          <VStack height="100%" spacing={3}>
            <Hide when={props.mechanic?.isMechanicHidden}>
              <Choice
                isRequired={props.mechanic?.isMechanicRequired}
                isLoading={props.mechanic?.areMechanicsLoading}
                isDisabled={props.basket.hasPendingRequest}
                label={i18n.t('entity.order.labels.mechanic')}
                value={defaultTo('', props.mechanic?.selectedMechanicId)}
                options={defaultTo([], getOptionsFromMechanics(props.mechanic?.mechanics))}
                onChange={props.mechanic?.onMechanicChange}
                errorMessage={props.mechanic?.mechanicError}
                data-testid={suffixTestId('assignedMechanic', props)}
              />
            </Hide>

            <Show when={props.basket.hasInvalidItems}>
              <Alert
                type="inline"
                variant="error"
                title={i18n.t('general.notifications.basketHasInvalidItems')}
                data-testid={suffixTestId('alert.hasInvalidItems', props)}
              />
            </Show>

            <HStack justify="space-between">
              <Hide when={isNilOrEmpty(basketItems)}>
                <Button
                  isDisabled={props.basket.hasPendingRequest}
                  type="button"
                  variant="link"
                  leftIcon="action/done_all"
                  title={
                    hasAllBasketItemsSelected
                      ? i18n.t('general.actions.unselectAll')
                      : i18n.t('general.actions.selectAll')
                  }
                  onClick={onSelectAllBasketItems}
                  data-testid={suffixTestId('actions.toggleSelect', props)}
                />
              </Hide>

              <Show when={hasSomeBasketItemsSelected}>
                <Button
                  isDisabled={props.basket.hasPendingRequest}
                  type="button"
                  variant="dangerLink"
                  leftIcon="navigation/close"
                  title={i18n.t('general.actions.remove')}
                  onClick={onBulkDeleteBasketItems}
                  data-testid={suffixTestId('actions.remove', props)}
                />
              </Show>
            </HStack>

            <Scroll flex={1} auto>
              <VStack height="100%" spacing={4}>
                <DataStatus
                  isLoading={props.basket.isLoading}
                  isError={props.basket.hasError}
                  isEmpty={basketItemsCount === 0}
                  emptyMessage={i18n.t('general.notifications.noItemsSelected')}
                  minHeight="100%"
                >
                  {basketItems?.map((basketItem, index) => (
                    <AfterSalesCorrectionBasketItem
                      key={basketItem.id}
                      item={basketItem}
                      onSelect={onSelectBasketItem}
                      onQuantityChange={props.onQuantityChange}
                      onEdit={props.onEdit}
                      onDelete={props.onDelete}
                      hasPendingRequest={props.basket.hasPendingRequest}
                      data-testid={suffixTestId(`basketItem-[${index}]`, props)}
                    />
                  ))}
                </DataStatus>
              </VStack>
            </Scroll>

            <Separator />

            <BasketSummary
              totalPrice={basketTotalPrice}
              data-testid={suffixTestId('summary', props)}
            />
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
